import { useEffect, useRef } from "react";
import toast from "react-hot-toast";

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_SERVER_URL;

/**
 * Global listener that keeps a WebSocket open while the user is logged in and
 * shows a real-time popup when a deposit is credited. It subscribes to the
 * user's personal channel (`user_<uid>`) — the same subscribe/broadcast
 * mechanism used for support tickets — and reconnects automatically.
 */
const DepositNotifier = () => {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    const rawUserId = localStorage.getItem("userId");
    // userId may be stored as a JSON string (e.g. "1000") or a plain value.
    const userId = String(rawUserId ?? "").replace(/"/g, "");
    if (!userId || !SOCKET_SERVER_URL) {
      console.warn(
        "[DepositNotifier] not connecting — userId:",
        userId,
        "socketUrl:",
        SOCKET_SERVER_URL
      );
      return;
    }

    let closedByUnmount = false;

    const connect = () => {
      let ws;
      try {
        ws = new WebSocket(SOCKET_SERVER_URL);
      } catch (e) {
        console.error("[DepositNotifier] WebSocket construct failed:", e);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        const channel = `user_${userId}`;
        ws.send(JSON.stringify({ type: "subscribe", ticketId: channel }));
        console.log("[DepositNotifier] connected, subscribed to", channel);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log("[DepositNotifier] message:", message);
          if (message?.type === "deposit_success") {
            toast.success(
              message.message || "Deposit received successfully!",
              { duration: 6000 }
            );
            // Let interested pages (dashboard, add-fund) refresh balances.
            window.dispatchEvent(
              new CustomEvent("deposit_success", { detail: message })
            );
          }
        } catch (e) {
          // Ignore non-JSON / unrelated messages.
        }
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {
          /* noop */
        }
      };

      ws.onclose = () => {
        if (!closedByUnmount) {
          reconnectRef.current = setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      closedByUnmount = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          /* noop */
        }
      }
    };
  }, []);

  return null;
};

export default DepositNotifier;
